import { Box, Button, Menu, MenuItem, Typography } from "@mui/material";
import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
  useRef,
} from "react";
import PolicyChangeCard from "./PolicyChangeCard";
import { useTranslation } from "react-i18next";
import { policyCardsData } from "../../../../constants/Constants";
import AddLienHolderForm from "../../../PolicyDetails/policyTabComponents/LienHolders/AddLienHolderForm/AddLienHolderForm";
import AddDriverForm from "../../../PolicyDetails/policyTabComponents/VehiclesAnDrivers/Drivers/AddDriverForm/AddDriverForm";
import AddVehicleForm from "../../../../common/AddVehicleForm/AddVehicleForm";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import {
  GetPolicyDetailsHeader,
  GetPolicyRisks,
} from "../../../../store/user/policyDetails/policyDetailsActions";
import {
  formatDateToYYYYMMDD,
  getCookie,
  isFeatureAvailable,
} from "../../../../utils/Utility";
import DriversMobileView from "../../../PolicyDetails/policyTabComponents/VehiclesAnDrivers/Drivers/DriversMobileView/DriversMobileView";
import { RootState } from "../../../../store/store";
import {
  IPolicyDriver,
  IPolicyVehicle,
} from "../../../../models/user/policyDetails/PolicyDetails";
import VehiclesMobileView from "../../../PolicyDetails/policyTabComponents/VehiclesAnDrivers/Vehicles/VehiclesMobileView/VehiclesMobileView";
import { DeleteOutline, EditOutlined } from "@mui/icons-material";
import AlertDialog from "../../../../components/AlertDialog/AlertDialog";
import UpdateGarageLocation from "../../../PolicyDetails/policyTabComponents/Coverages/UpdateGarageLocation/UpdateGarageLocation";
import {
  postRemoveDriver,
  PostRemoveVehicle,
} from "../../../../store/user/policyChange/policyChangeActions";
import MessageDialog from "../../../../components/MessageDialog/MessageDialog";
import EditDriverForm from "../../../PolicyDetails/policyTabComponents/VehiclesAnDrivers/Drivers/EditDriverForm/EditDriverForm";
import UpdatePropertyDetails from "./UpdatePropertyDetails/UpdatePropertyDetails";
import UpdateBuildingSafetyFeatures from "./UpdateBuildingSafetyFeatures/UpdateBuildingSafetyFeatures";
import UpdateUsageAndOccupancy from "./UpdateUsageAndOccupancy/UpdateUsageAndOccupancy";
import UpdateDwellingDetails from "./UpdateDwellingDetails/UpdateDwellingDetails";
import UpdateSafetySecurityFeatures from "./UpdateSafetySecurityFeatures/UpdateSafetySecurityFeatures";
import UpdateFOUsageAndOccupancyComp from "./UpdateUsageAndOccupancy/UpdateFOUsageAndOccupancy";

interface IPolicyChange {
  setPolicyChangeDrawerTitle: (title: string) => void;
  lobCode: string;
  policyId: string;
  closePolicyChangeDrawer: () => void;
  setChangePolicyOpen: Dispatch<SetStateAction<boolean>>;
  resetPolicyChangeState?: React.MutableRefObject<(() => void) | null>;
}

const PolicyChange: FC<IPolicyChange> = ({
  setPolicyChangeDrawerTitle,
  lobCode,
  policyId,
  closePolicyChangeDrawer,
  setChangePolicyOpen,
  resetPolicyChangeState,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [selectedPolicyChange, setSelectedPolicyChange] = useState("");
  const [showPolicyChangeForm, setShowPolicyChangeForm] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [alertDialogOpen, setAlertDialogOpen] = useState<boolean>(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);

  const { features } = useAppSelector((state: RootState) => state.features);

  const [selectedDriver, setSelectedDriver] = useState<IPolicyDriver | null>(
    null
  );
  const [selectedVehicle, setSelectedVehicle] = useState<IPolicyVehicle | null>(
    null
  );
  const [updateGarageLocation, setUpdateGarageLocation] =
    useState<IPolicyVehicle | null>(null);
  const [editDriver, setEditDriver] = useState<IPolicyDriver | null>(null);

  const open = Boolean(anchorEl);

  const { policyVehicles, policyDrivers } = useAppSelector(
    (store: RootState) => store.policyDetails
  );
  const { policyDetailsHeader } = useAppSelector(
    (store: RootState) => store.policyDetails
  );
  const { userDetails } = useAppSelector((state: RootState) => state.userAuth);

  const { isSuccess, isError, calledReducerType } = useAppSelector(
    (state) => state.common
  );
  const carrier = getCookie("carrier");
  const system = getCookie("system");

  const handleMoreClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    VehicleOrDriver: any
  ) => {
    setAnchorEl(event.currentTarget);
    selectedPolicyChange === "Remove Vehicle" ||
    selectedPolicyChange === "Change Garage Location"
      ? setSelectedVehicle(VehicleOrDriver)
      : (selectedPolicyChange === "Remove Driver" ||
          selectedPolicyChange === "Edit Driver") &&
        setSelectedDriver(VehicleOrDriver);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteDriverClick = () => {
    handleMenuClose();
    setAlertDialogOpen(true);
  };

  const handleProceed = () => {
    if (!selectedPolicyChange) return;
    switch (selectedPolicyChange) {
      case "Add Lienholder":
      case "Add Vehicle":
      case "Remove Vehicle":
      case "Add Driver":
      case "Edit Driver":
      case "Remove Driver":
      case "Change Garage Location":
      case "Update Property Details":
      case "Update Building Safety Features":
      case "Update Usage & Occupancy":
      case "Update Dwelling Details":
      case "Update Safety & Security Features":
      case "Update Occupancy & Usage":

        if (policyId && carrier && system && lobCode) {
          dispatch(GetPolicyRisks({ policyId, carrier, system, lobCode }));
          dispatch(
            GetPolicyDetailsHeader({ policyId, carrier, system, lobCode })
          );
        }
        break;
      default:
        break;
    }

    setShowPolicyChangeForm(true);
    setPolicyChangeDrawerTitle(selectedPolicyChange);
  };

  const renderPolicyChangeComponent = (selectedPolicy: string) => {
    switch (selectedPolicy) {
      case "Add Vehicle":
        return (
          <AddVehicleForm
            open={true}
            setOpen={setShowPolicyChangeForm}
            changePolicyId={policyId}
            changePolicyLobCode={lobCode}
            onErrorClose={() => {
              setShowPolicyChangeForm(false);
              setPolicyChangeDrawerTitle("");
            }}
          />
        );
      case "Remove Vehicle":
        return (
          <Box sx={{ padding: "1.25rem" }}>
            <VehiclesMobileView
              vehicles={policyVehicles}
              displayMoreOptions
              handleMoreClick={handleMoreClick}
            />
          </Box>
        );
      case "Add Driver":
        return (
          <AddDriverForm
            open={true}
            setOpen={setShowPolicyChangeForm}
            changePolicyId={policyId}
            changePolicyLobCode={lobCode}
          />
        );
      case "Edit Driver":
        return (
          <>
            {selectedPolicyChange === "Edit Driver" && !editDriver ? (
              <Box sx={{ padding: "1.25rem" }}>
                <DriversMobileView
                  drivers={policyDrivers}
                  handleMoreClick={handleMoreClick}
                  displayMoreOptions={true}
                />
              </Box>
            ) : (
              <EditDriverForm
                driver={selectedDriver as IPolicyDriver}
                open={true}
                setOpen={setChangePolicyOpen}
              />
            )}
          </>
        );
      case "Remove Driver":
        return (
          <Box sx={{ padding: "1.25rem" }}>
            <DriversMobileView
              drivers={policyDrivers}
              handleMoreClick={handleMoreClick}
              displayMoreOptions={true}
            />
          </Box>
        );
      case "Add Lienholder":
        return (
          <AddLienHolderForm
            open={true}
            setOpen={setShowPolicyChangeForm}
            changePolicyId={policyId}
            changePolicyLobCode={lobCode}
          />
        );
      case "Change Garage Location":
        return (
          <>
            {!!updateGarageLocation ? (
              <UpdateGarageLocation
                open={true}
                setOpen={setChangePolicyOpen}
                vehicle={updateGarageLocation as IPolicyVehicle}
              />
            ) : (
              <Box sx={{ padding: "1.25rem" }}>
                <VehiclesMobileView
                  vehicles={policyVehicles}
                  displayMoreOptions
                  handleMoreClick={handleMoreClick}
                />
              </Box>
            )}
          </>
        );
      case "Update Property Details":
        return (
          <UpdatePropertyDetails
            open={true}
            setOpen={setShowPolicyChangeForm}
            changePolicyId={policyId}
            changePolicyLobCode={lobCode}
            onClose={closePolicyChangeDrawer}
          />
        );
      case "Update Building Safety Features":
        return (
          <UpdateBuildingSafetyFeatures
            open={true}
            setOpen={setShowPolicyChangeForm}
            changePolicyId={policyId}
            changePolicyLobCode={lobCode}
            onClose={closePolicyChangeDrawer}
          />
        );
      case "Update Usage & Occupancy":
        return (
          <UpdateUsageAndOccupancy
            open={true}
            setOpen={setShowPolicyChangeForm}
            changePolicyId={policyId}
            changePolicyLobCode={lobCode}
            onClose={closePolicyChangeDrawer}
          />
        );
      case "Update Dwelling Details":
        return (
          <UpdateDwellingDetails
            open={true}
            setOpen={setShowPolicyChangeForm}
            changePolicyId={policyId}
            changePolicyLobCode={lobCode}
            onClose={closePolicyChangeDrawer}
          />
        );
      case "Update Safety & Security Features":
        return (
          <UpdateSafetySecurityFeatures
            open={true}
            setOpen={setShowPolicyChangeForm}
            changePolicyId={policyId}
            changePolicyLobCode={lobCode}
            onClose={closePolicyChangeDrawer}
          />
        );
      case "Update Occupancy & Usage":
        return (
          <UpdateFOUsageAndOccupancyComp
            open={true}
            setOpen={setShowPolicyChangeForm}
            changePolicyId={policyId}
            changePolicyLobCode={lobCode}
            onClose={closePolicyChangeDrawer}
          />
        );
      default:
        return (
          <Typography sx={{ padding: "2rem", textAlign: "center" }}>
            Form for {selectedPolicy} is not implemented
          </Typography>
        );
    }
  };

  const handleAlertDialogCancelClick = () => {
    setAlertDialogOpen(false);
  };

  const handleMessageModalClose = useCallback(() => {
    setMessageDialogOpen(false);
    closePolicyChangeDrawer();
  }, []);

  const handleAlertDialogOkClick = () => {
    setAlertDialogOpen(false);
    if (
      selectedPolicyChange === "Remove Driver" &&
      selectedDriver &&
      policyId &&
      userDetails &&
      userDetails.customerId &&
      policyDetailsHeader &&
      policyDetailsHeader.carrierPolicyNumber &&
      system &&
      carrier &&
      lobCode
    ) {
      dispatch(
        postRemoveDriver({
          carrierPolicyNo: policyDetailsHeader.carrierPolicyNumber,
          changeDt: formatDateToYYYYMMDD(new Date()),
          customerId: userDetails.customerId,
          detailDescription: "Remove driver from the policy",
          driverInfo: {
            driverAge: selectedDriver?.driverAge.toString(),
            dateOfBirth: selectedDriver?.dateOfBirth,
            genderCd: selectedDriver?.genderCd,
            givenName: selectedDriver?.givenName,
            licensedStateProvCd: selectedDriver?.licensedStateProvCd,
            licenseDt: selectedDriver?.licenseDt,
            licenseNumber: selectedDriver?.licenseNumber,
            maritalStatus: selectedDriver?.maritalStatus,
            otherGivenName: selectedDriver?.otherGivenName,
            relationshipToInsuredCd: selectedDriver?.relationshipToInsuredCd,
            surName: selectedDriver?.surName,
          },
          policyRef: policyId,
          system,
          carrier,
          lobCode,
        })
      );
    }

    if (
      selectedPolicyChange === "Remove Vehicle" &&
      selectedVehicle &&
      policyId &&
      userDetails &&
      userDetails.customerId &&
      policyDetailsHeader &&
      policyDetailsHeader.carrierPolicyNumber &&
      carrier &&
      system &&
      lobCode
    ) {
      dispatch(
        PostRemoveVehicle({
          additionalInterestDetails: {
            loanNumber: "",
            address: null,
            interestName: "",
            interestTypeCd: "",
            status: "",
            phoneInfo: "",
          },
          carrierPolicyNo: policyDetailsHeader?.carrierPolicyNumber,
          changeDt: formatDateToYYYYMMDD(new Date()),
          customerId: userDetails.customerId,
          detailDescription: t("Removing vehicle from the policy"),
          policyRef: policyId,
          carrier,
          system,
          lobCode,
          vehicle: {
            make: selectedVehicle?.make,
            model: selectedVehicle?.model,
            manufactureYr: selectedVehicle.manufactureYr,
            vehicleType: selectedVehicle.vehicleType,
            vin: selectedVehicle.vin,
            address: null,
          },
        })
      );
    }
  };

  const handleUpdateGarageLocation = () => {
    setAnchorEl(null);
    if (selectedVehicle) setUpdateGarageLocation(selectedVehicle);
  };
  const handleEditDriverFormOpen = () => {
    setAnchorEl(null);
    if (selectedDriver) setEditDriver(selectedDriver);
  };

  const resetInternalState = () => {
    setSelectedPolicyChange("");
    setShowPolicyChangeForm(false);
    setSelectedDriver(null);
    setSelectedVehicle(null);
    setUpdateGarageLocation(null);
    setEditDriver(null);
    setAnchorEl(null);
  };

  useEffect(() => {
    if (resetPolicyChangeState) {
      resetPolicyChangeState.current = resetInternalState;
    }
  }, [resetPolicyChangeState]);

  useEffect(() => {
    const validTypes = [
      "policyChange/postRemoveDriver",
      "policyChange/PostAddVehicle",
      "policyChange/PostRemoveVehicle",
      "policyChange/PostAddDriver",
      "policyChange/AddVehicleFinanceCompany",
    ];

    if (
      (isSuccess || isError) &&
      validTypes.includes(calledReducerType || "")
    ) {
      setMessageDialogOpen(true);
    }
  }, [isSuccess, isError, calledReducerType]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      {!showPolicyChangeForm ? (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "0.8rem" }}>
            <Typography>
              {t("Select the change you would like to make")}
            </Typography>
            {policyCardsData
              ?.filter((card) => card?.lobCode === lobCode)
              .map((card, index) => (
                <PolicyChangeCard
                  key={index}
                  title={card.title}
                  lobCode={card.lobCode}
                  checkboxList={card.checkboxList}
                  selectedPolicyChange={selectedPolicyChange}
                  setSelectedPolicyChange={setSelectedPolicyChange}
                />
              ))}
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              justifyContent: "flex-end",
            }}
          >
            <Button
              id="Change-Policy-Proceed-btn"
              data-test-id="test-Change-Policy-Proceed"
              aria-label="Proceed"
              variant="contained"
              color="primary"
              onClick={handleProceed}
              disabled={!selectedPolicyChange}
            >
              {t("Proceed")}
            </Button>
          </Box>
        </>
      ) : (
        renderPolicyChangeComponent(selectedPolicyChange)
      )}
      <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
        {selectedPolicyChange === "Edit Driver" &&
          isFeatureAvailable({
            featureCodes: ["lob_pa_policies_edit_driver"],
            features,
          }) && (
            <MenuItem onClick={handleEditDriverFormOpen}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <EditOutlined sx={{ fontSize: "16px" }} />
                <Typography>{t("Edit")}</Typography>
              </Box>
            </MenuItem>
          )}

        {["Remove Driver", "Remove Vehicle"].includes(selectedPolicyChange) &&
          isFeatureAvailable({
            featureCodes: [
              selectedPolicyChange === "Remove Driver"
                ? "lob_pa_policies_delete_driver"
                : "lob_pa_policies_delete_vehicle",
            ],
            features,
          }) && (
            <MenuItem
              onClick={handleDeleteDriverClick}
              disabled={
                (selectedPolicyChange === "Remove Driver" &&
                  policyDrivers?.length === 1) ||
                (selectedPolicyChange === "Remove Vehicle" &&
                  policyVehicles?.length === 1)
              }
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <DeleteOutline sx={{ fontSize: "16px" }} />
                <Typography>{t("Remove")}</Typography>
              </Box>
            </MenuItem>
          )}

        {selectedPolicyChange === "Change Garage Location" &&
          isFeatureAvailable({
            featureCodes: ["lob_pa_policies_update_garage_location"],
            features,
          }) && (
            <MenuItem onClick={handleUpdateGarageLocation}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <EditOutlined sx={{ fontSize: "16px" }} />
                <Typography>{t("Change Garage Location")}</Typography>
              </Box>
            </MenuItem>
          )}
      </Menu>
      <AlertDialog
        handleCancelClick={handleAlertDialogCancelClick}
        handleOkClick={handleAlertDialogOkClick}
        open={alertDialogOpen}
        title={
          selectedPolicyChange === "Remove Driver"
            ? t("Removing this driver will end its coverage under this policy")
            : t("Removing this vehicle will end its coverage under this policy")
        }
      />
      <MessageDialog
        handleClose={handleMessageModalClose}
        open={messageDialogOpen}
      />
    </Box>
  );
};

export default PolicyChange;
